/**
 * Frozen tilemap format constants — the contract shared with JoystickEngine
 * (_docs/tilemap/01 §3). Changing any of these is a versioned format event,
 * never a refactor: the same numbers are assumed by the engine's C++ runtime
 * (plan steps 45–47), the PHP codec (App\Support\RleCodec), the .dgpack
 * bundle, and every exported `.jtilemap`.
 */

/** Tiles per chunk edge. A chunk is CHUNK_SIZE × CHUNK_SIZE tiles. */
export const CHUNK_SIZE = 32;

/** Flat tile count in one chunk (1024). */
export const CHUNK_AREA = CHUNK_SIZE * CHUNK_SIZE;

/** log2(CHUNK_SIZE) — the shift for floor-division cell→chunk math. */
export const CHUNK_SHIFT = 5;

/**
 * The empty tile. id 0 is always empty; a non-empty tile's atlas index is
 * `id - 1` (engine 45.2). Never store a chunk that is all-empty.
 */
export const EMPTY_TILE = 0;

/** A tile id (u32). 0 = empty; otherwise atlasIndex + 1. */
export type TileID = number;

/**
 * Floor-divide a cell coordinate to its chunk coordinate. Arithmetic shift
 * is correct for negatives (cell −1 → chunk −1), matching the engine's
 * `cell.x >> 5`.
 */
export function cellToChunk(cell: number): number {
    return cell >> CHUNK_SHIFT;
}

/** Local index (0..CHUNK_AREA-1) of a cell within its chunk, row-major. */
export function cellLocalIndex(cellX: number, cellY: number): number {
    const lx = cellX - (cellToChunk(cellX) << CHUNK_SHIFT);
    const ly = cellY - (cellToChunk(cellY) << CHUNK_SHIFT);
    return ly * CHUNK_SIZE + lx;
}
